import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabase } from '../../../supabaseClient';
import toast from 'react-hot-toast';

/**
 * Composant de formulaire pour créer ou modifier un utilisateur
 * @returns {JSX.Element} Formulaire utilisateur
 */
const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    lastname: '',
    firstname: '',
    birth_date: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipcode: '',
    country: '',
    host_name: ''
  });
  
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /**
   * Récupère les données de l'utilisateur depuis Supabase
   */
  useEffect(() => {
    const fetchUser = async () => {
      if (isEditMode) {
        try {
          const supabase = getSupabase();
          
          // Récupération des données utilisateur depuis Supabase
          const { data, error } = await supabase
            .from('users')
            .select('*')
            .eq('id', id)
            .single();
          
          if (error) {
            throw error;
          }
          
          if (!data) {
            throw new Error('Utilisateur non trouvé');
          }
          
          // Mise à jour du formulaire avec les données réelles
          setFormData({
            lastname: data.lastname || '',
            firstname: data.firstname || '',
            birth_date: data.birth_date || '',
            email: data.email || '',
            phone: data.phone || '',
            address: data.address || '',
            city: data.city || '',
            zipcode: data.zipcode || '',
            country: data.country || '',
            host_name: data.host_name || ''
          });
          
          setLoading(false);
        } catch (error) {
          console.error('Erreur lors du chargement des données utilisateur:', error);
          setError('Impossible de charger les données de l\'utilisateur');
          setLoading(false);
        }
      }
    };

    fetchUser();
  }, [id, isEditMode]);

  /**
   * Gère les changements dans les champs du formulaire
   * @param {Event} e - L'événement de changement
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Gère la soumission du formulaire pour créer ou mettre à jour un utilisateur
   * @param {Event} e - L'événement de soumission du formulaire
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const supabase = getSupabase();
      
      // Préparation des données à envoyer
      const userData = {
        lastname: formData.lastname,
        firstname: formData.firstname,
        birth_date: formData.birth_date,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        zipcode: formData.zipcode,
        country: formData.country,
        host_name: formData.host_name || null,
        updated_at: new Date().toISOString()
      };
      
      // Si c'est un nouveau utilisateur, on ajoute la date de création
      if (!isEditMode) {
        userData.created_at = new Date().toISOString();
      }
      
      // Mise à jour ou création de l'utilisateur
      const { error } = isEditMode 
        ? await supabase.from('users').update(userData).eq('id', id)
        : await supabase.from('users').insert(userData);

      if (error) {
        console.error('Erreur Supabase:', error);
        toast.error(`Erreur: ${error.message || 'Erreur lors de la sauvegarde de l\'utilisateur'}`);
        return;
      }

      toast.success(`Utilisateur ${isEditMode ? 'mis à jour' : 'créé'} avec succès`);
      
      // Rediriger vers la fiche du contact après un court délai si c'est une mise à jour
      // ou vers la liste des utilisateurs si c'est une création
      setTimeout(() => {
        if (isEditMode) {
          navigate(`/admin/users/view/${id}`);
        } else {
          navigate('/admin/users');
        }
      }, 2000);
    } catch (err) {
      console.error('Erreur:', err);
      toast.error(`Une erreur est survenue: ${err.message || 'Erreur inconnue'}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">
        {isEditMode ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}
      </h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="lastname">
              Nom
            </label>
            <input
              id="lastname"
              name="lastname"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.lastname}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="firstname">
              Prénom
            </label>
            <input
              id="firstname"
              name="firstname"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.firstname}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="tracking-input w-full"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birth_date">
              Date de naissance
            </label>
            <input
              id="birth_date"
              name="birth_date"
              type="date"
              required
              max={new Date().toISOString().slice(0, 10)}
              className="tracking-input w-full"
              value={formData.birth_date}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone">
              Téléphone
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              required
              className="tracking-input w-full"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="address">
              Adresse
            </label>
            <input
              id="address"
              name="address"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="city">
              Ville
            </label>
            <input
              id="city"
              name="city"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.city}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="zipcode">
              Code postal
            </label>
            <input
              id="zipcode"
              name="zipcode"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.zipcode}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="country">
              Pays
            </label>
            <input
              id="country"
              name="country"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.country}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="host_name">
              Nom et prénom de l&apos;hébergeant (optionnel)
            </label>
            <input
              id="host_name"
              name="host_name"
              type="text"
              className="tracking-input w-full"
              value={formData.host_name}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="flex justify-end mt-6 space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-modern btn-primary-modern"
          >
            {isEditMode ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
